import { useState, useEffect, useCallback } from "react";
import Cookies from "universal-cookie";
import { toast } from "react-toastify";
import { axiosReq } from "../../commons/axiosReq";
import AddJobModal from "../../components/User/AddJobModal";
import AddJobApplyModal from "../../components/User/AddJobApplyModal";
import Select from "react-select";
import Person from "../../assets/icon/NewsIcons/Login/LoginPerson";
import { useLanguage } from "../../i18n/LanguageContext";

const colourStyles = {
    control: (base) => ({ ...base, backgroundColor: "white", minHeight: 35, height: 35 }),
    valueContainer: (base) => ({ ...base, height: 35, padding: "0 8px" }),
    option: (base) => ({ ...base, backgroundColor: "white", color: "#000" }),
};

const copy = {
    fa: { title: "مدیریت شغل‌ها", newApplication: "درخواست جدید", addJob: "افزودن شغل", search: "جستجوی شغل ...", country: "فیلتر کشور", jobs: "شغل", noJobs: "شغلی ثبت نشده است.", link: "لینک", deadline: "مهلت", applications: "وضعیت درخواست‌ها", updated: "وضعیت بروزرسانی شد", updateFailed: "بروزرسانی وضعیت ناموفق بود", untitled: "بدون عنوان", sent: "ایمیل با موفقیت ارسال شد", sendFailed: "ارسال ایمیل ناموفق بود", details: "جزئیات درخواست", sending: "در حال ارسال...", send: "ارسال", columns: { Draft: "پیش‌نویس", Sent: "ارسال شده", Interview: "مصاحبه", Offer: "پیشنهاد", Rejected: "رد شده" } },
    en: { title: "Job application tracker", newApplication: "New application", addJob: "Add job", search: "Search jobs...", country: "Filter by country", jobs: "jobs", noJobs: "No jobs have been added.", link: "Open listing", deadline: "Deadline", applications: "Application status", updated: "Status updated", updateFailed: "Could not update the status", untitled: "Untitled", sent: "Email sent successfully", sendFailed: "Could not send the email", details: "Application details", sending: "Sending...", send: "Send", columns: { Draft: "Draft", Sent: "Sent", Interview: "Interview", Offer: "Offer", Rejected: "Rejected" } }
};
const columnKeys = ["Draft", "Sent", "Interview", "Offer", "Rejected"];

const Jobs = () => {
    const { language, direction, isEnglish } = useLanguage();
    const t = copy[language];
    const columns = columnKeys.map((key) => ({ key, label: t.columns[key] }));
    const [jobs, setJobs] = useState([]);
    const [applications, setApplications] = useState([]);
    const [jobsSelect, setJobsSelect] = useState([]);
    const [showAddJob, setShowAddJob] = useState(false);
    const [showAddApply, setShowAddApply] = useState(false);
    const [detail, setDetail] = useState(null);
    const [token, setToken] = useState("");
    const [draggedId, setDraggedId] = useState(null);
    const [sending, setSending] = useState(false);
    const [reCheck, setRecheck] = useState(false);
    const [countries, setCountries] = useState([]);
    const [searchJobs, setSearchJobs] = useState("");
    const [filterJobCountry, setFilterJobCountry] = useState(null);

    const readToken = () => {
        const cookies = new Cookies();
        setToken(cookies.get("token") || "");
    };

    const fetchJobs = async () => {
        const res = await axiosReq("Job", "get");
        if (typeof res === "string") {
            setJobs([]);
            setJobsSelect([]);
            return;
        }
        const list = Array.isArray(res) ? res : res?.data || [];
        setJobs(list);
    };

    const fetchApplications = async () => {
        const res = await axiosReq("Job/Apply", "get");
        if (typeof res === "string") {
            setApplications([]);
            return;
        }
        const list = Array.isArray(res) ? res : res?.data || [];
        setApplications(list);
    };

    const fetchCountries = async () => {
        const res = await axiosReq("University/GetCountries", "get");
        if (typeof res === "string") return;
        const list = Array.isArray(res) ? res : res?.data || [];
        setCountries(
            list.map((c) => ({
                value: c.countryId,
                label: c.name,
            }))
        );
    };

    const loadAll = useCallback(() => {
        fetchJobs();
        fetchApplications();
        fetchCountries();
    }, []);

    useEffect(() => {
        readToken();
        loadAll();
    }, [loadAll, reCheck]);

    useEffect(() => {
        setJobsSelect(
            jobs
                .filter((j) => {
                    if (searchJobs) {
                        const s = searchJobs.toLowerCase();
                        if (
                            !(j.title || "").toLowerCase().includes(s) &&
                            !(j.company || "").toLowerCase().includes(s)
                        ) return false;
                    }
                    if (filterJobCountry && j.countryId !== filterJobCountry.value) return false;
                    return true;
                })
                .map((j) => ({
                    value: j.jobId,
                    label: `${j.title || ""} - ${j.company || ""}`.trim(),
                }))
        );
    }, [jobs, searchJobs, filterJobCountry]);

    const filteredJobs = jobs.filter((j) => {
        if (searchJobs) {
            const s = searchJobs.toLowerCase();
            if (
                !(j.title || "").toLowerCase().includes(s) &&
                !(j.company || "").toLowerCase().includes(s) &&
                !(j.location || "").toLowerCase().includes(s) &&
                !(j.tagsCsv || "").toLowerCase().includes(s)
            ) return false;
        }
        if (filterJobCountry && j.countryId !== filterJobCountry.value) return false;
        return true;
    });

    const onDragStart = (id) => setDraggedId(id);

    const onDrop = async (status) => {
        if (!draggedId) return;
        const card = applications.find((a) => (a.jobApplyId || a.id) === Number(draggedId) || (a.jobApplyId || a.id) === draggedId);
        setDraggedId(null);
        if (!card) return;

        const body = {
            jobApplyId: card.jobApplyId || card.id,
            jobId: card.jobId,
            userId: card.userId ?? null,
            coverLetter: card.coverLetter || "",
            subject: card.subject || "",
            status: status,
        };
        const res = await axiosReq("Job/Apply", "put", body);
        if (res?.status === 200 || res?.status === 204 || res?.status === 201) {
            toast.success(t.updated, { rtl: !isEnglish });
            fetchApplications();
        } else {
            toast.error(t.updateFailed, { rtl: !isEnglish });
        }
    };

    const cardsByStatus = (status) =>
        applications.filter(
            (a) => (a.status || a.Status || a.status) === status
        );

    const titleField = (a) => a.subject || a.job?.title || t.untitled;

    const sendJobApply = async () => {
        if (!detail) return;
        const id = detail.jobApplyId || detail.id;
        if (!id) return;
        setSending(true);
        const res = await axiosReq(`Job/Apply/Send/${id}`, "post");
        setSending(false);
        if (res?.status === 200 || res?.status === 201) {
            toast.success(t.sent, { rtl: !isEnglish });
            setDetail(null);
            fetchApplications();
        } else {
            const msg =
                (typeof res?.data === "string"
                    ? res.data
                    : res?.data?.error || res?.data?.error || res?.data?.message) ||
                t.sendFailed;
            toast.error(msg, { rtl: !isEnglish });
        }
    };

    return (
        <div className="p-[20px]" dir={direction}>
            <div className="flex items-center justify-between border-b-2 border-borderGray p-[15px]">
                <p className="text-[16px] font-IRANYekanBold text-mainColor2">
                    {t.title}
                </p>
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowAddApply(true)}
                        className="h-[45px] w-[160px] bg-infinity-gradient shadow-norm-shadow rounded-[15px] flex justify-center items-center"
                    >
                        <p className="font-IRANYekanBold text-[15px] text-white">
                            {t.newApplication}
                        </p>
                    </button>
                    <button
                        onClick={() => setShowAddJob(true)}
                        className="h-[45px] w-[150px] bg-infinity-gradient shadow-norm-shadow rounded-[15px] flex justify-center items-center"
                    >
                        <p className="font-IRANYekanBold text-[15px] text-white">
                            {t.addJob}
                        </p>
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-3 items-center mt-4 mb-1">
                <div className="w-[200px] h-[35px] rounded-[10px] shadow-search-shadow flex items-center px-[10px]">
                    <input
                        onChange={(e) => setSearchJobs(e.target.value)}
                        className="font-IRANYekanNormal text-[13px] focus:outline-none placeholder-lightGray w-full"
                        type="text"
                        placeholder={t.search}
                    />
                </div>
                <div className="w-[180px]">
                    <Select
                        options={countries}
                        value={filterJobCountry}
                        onChange={setFilterJobCountry}
                        placeholder={t.country}
                        isSearchable={true}
                        isClearable={true}
                        styles={colourStyles}
                    />
                </div>
                <span className="text-[12px] text-mainColor2 font-IRANYekanNormal">
                    {filteredJobs.length} {t.jobs}
                </span>
            </div>

            <div className="mt-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {filteredJobs.length === 0 ? (
                        <p className="text-[13px] text-mainColor2 col-span-3">
                            {t.noJobs}
                        </p>
                    ) : (
                        filteredJobs.map((j, idx) => (
                            <div
                                key={j.jobId || idx}
                                className="rounded-[15px] border-2 border-borderGray p-[15px] bg-white"
                            >
                                <div className="flex items-center justify-between">
                                    <p className="font-IRANYekanBold text-[15px] text-mainColor2">
                                        {j.title}
                                    </p>
                                    <Person />
                                </div>
                                <p className="text-[13px] text-mainColor2 mt-1">
                                    {j.company}
                                </p>
                                <p className="text-[12px] text-mainColor2 mt-1">
                                    {j.location}{j.countryName ? ` - ${j.countryName}` : ""}
                                </p>
                                {j.url ? (
                                    <a
                                        href={j.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[12px] text-blue-500 mt-1 inline-block"
                                        dir="ltr"
                                    >
                                        {t.link}
                                    </a>
                                ) : null}
                                {j.deadline ? (
                                    <p className="text-[12px] text-mainColor2 mt-1">
                                        {t.deadline}: {j.deadline}
                                    </p>
                                ) : null}
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="mt-8">
                <p className="text-[14px] font-IRANYekanBold text-mainColor2 mb-2">
                    {t.applications}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    {columns.map((col) => (
                        <div
                            key={col.key}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => onDrop(col.key)}
                            className="rounded-[15px] border-2 border-borderGray bg-backGray p-[12px] min-h-[200px]"
                        >
                            <p className="text-[13px] font-IRANYekanBold text-mainColor2 mb-[10px]">
                                {col.label} ({cardsByStatus(col.key).length})
                            </p>
                            <div className="flex flex-col gap-2">
                                {cardsByStatus(col.key).map((a, idx) => {
                                    const id = a.jobApplyId || a.id;
                                    return (
                                        <div
                                            key={id || idx}
                                            draggable
                                            onDragStart={() => onDragStart(id)}
                                            onClick={() => setDetail(a)}
                                            className="cursor-pointer rounded-[10px] bg-white border border-borderGray p-[10px] shadow-sm"
                                        >
                                            <p className="text-[13px] font-IRANYekanBold text-mainColor2">
                                                {titleField(a)}
                                            </p>
                                            {a.job?.title ? (
                                                <p className="text-[11px] text-mainColor2 mt-1">
                                                    {a.job.title}
                                                </p>
                                            ) : null}
                                        </div>
                                    );
                                })}
                                {cardsByStatus(col.key).length === 0 ? (
                                    <p className="text-[11px] text-mainColor2">—</p>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {showAddJob ? (
                <AddJobModal
                    setShowModal={setShowAddJob}
                    setRecheck={setRecheck}
                    reCheck={reCheck}
                    onSaved={fetchJobs}
                    countries={countries}
                    language={language}
                />
            ) : null}
            {showAddApply ? (
                <AddJobApplyModal
                    setShowModal={setShowAddApply}
                    setRecheck={setRecheck}
                    reCheck={reCheck}
                    onSaved={fetchApplications}
                    jobs={jobsSelect}
                    token={token}
                    language={language}
                />
            ) : null}

            {detail ? (
                <div
                    className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-[1200] outline-none focus:outline-none"
                    onClick={() => setDetail(null)}
                >
                    <div
                        className="relative w-[50%] max-w-[600px] my-5 mx-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="border-0 rounded-[20px] p-[25px] shadow-lg bg-white outline-none flex flex-col">
                            <div className="flex justify-between items-center border-b-2 pb-[15px] border-borderGray">
                                <p className="text-[15px] font-IRANYekanBold text-mainColor2">
                                    {t.details}
                                </p>
                                <button onClick={() => setDetail(null)}>✕</button>
                            </div>
                            <div className="mt-3 max-h-[60vh] overflow-y-auto">
                                <p className="text-[14px] font-IRANYekanBold text-mainColor2">
                                    {detail.subject}
                                </p>
                                {detail.job?.title ? (
                                    <p className="text-[12px] text-mainColor2 mt-1">
                                        {detail.job.title}
                                    </p>
                                ) : null}
                                {detail.coverLetter ? (
                                    <div
                                        className="text-[13px] mt-2 prose"
                                        dangerouslySetInnerHTML={{
                                            __html: detail.coverLetter,
                                        }}
                                    />
                                ) : null}
                            </div>
                            <div className="flex justify-end mt-4">
                                <button
                                    onClick={sendJobApply}
                                    disabled={sending}
                                    className="h-[40px] w-[140px] bg-infinity-gradient rounded-[10px] flex justify-center items-center"
                                >
                                    <p className="font-IRANYekanBold text-[14px] text-white">
                                        {sending ? t.sending : t.send}
                                    </p>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="opacity-25 fixed inset-0 z-40 bg-black" />
                </div>
            ) : null}
        </div>
    );
};

export default Jobs;
